import { useState } from "react";
import { motion } from "framer-motion";
import { Code, Lock, Unlock, Copy, CheckCircle, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";

const API_SECTIONS = [
  {
    title: "Autenticación",
    endpoints: [
      { method: "POST", path: "/api/auth/login", desc: "Iniciar sesión y obtener token JWT", auth: false, body: '{"email": "tu@email.com", "password": "tu_password"}', response: '{"token": "eyJ...", "user": {...}}' },
      { method: "POST", path: "/api/auth/register", desc: "Registrar nuevo usuario", auth: false, body: '{"email": "...", "name": "...", "password": "..."}' },
    ]
  },
  {
    title: "Envíos",
    endpoints: [
      { method: "POST", path: "/api/shipping/compare", desc: "Comparar tarifas de 6 paqueterías", auth: false, params: "origin_cp, destination_cp, weight, length, width, height" },
      { method: "GET", path: "/api/shipments/track/{tracking}", desc: "Rastrear envío por número de guía", auth: false },
      { method: "POST", path: "/api/shipments/bulk", desc: "Crear envíos masivos desde CSV", auth: true, body: "FormData con archivo CSV (columnas: origin_cp, destination_cp, recipient_name, weight)" },
      { method: "POST", path: "/api/shipping/volumetric-calculator", desc: "Calcular peso volumétrico", auth: false, params: "length, width, height, actual_weight" },
    ]
  },
  {
    title: "Guías Prepago",
    endpoints: [
      { method: "POST", path: "/api/payments/checkout/guides", desc: "Comprar paquete de guías (Stripe)", auth: true, body: '{"origin_url": "...", "package_id": "pack_25"}' },
      { method: "GET", path: "/api/guides/balance", desc: "Consultar saldo de guías", auth: true },
      { method: "POST", path: "/api/guides/generate", desc: "Generar guía de envío", auth: true, body: '{"carrier_id": "dhl", "service_type": "standard", "origin_cp": "06600", "destination_cp": "44100", "weight": "2", "recipient_name": "Juan"}' },
      { method: "GET", path: "/api/guides/{id}/label", desc: "Descargar etiqueta PDF con código de barras", auth: true },
    ]
  },
  {
    title: "Inventario Multicanal",
    endpoints: [
      { method: "GET", path: "/api/inventory/products", desc: "Listar productos", auth: true },
      { method: "POST", path: "/api/inventory/products", desc: "Crear producto y publicar en marketplaces", auth: true, body: '{"name": "...", "sku": "ABC123", "price": 99.00, "stock": 50, "publish_to": ["mercadolibre"]}' },
      { method: "GET", path: "/api/marketplace/orders", desc: "Obtener pedidos de todos los marketplaces", auth: true },
    ]
  },
  {
    title: "Webhooks",
    endpoints: [
      { method: "POST", path: "/api/webhooks/config", desc: "Registrar webhook para notificaciones", auth: true, body: '{"url": "https://tu-servidor.com/webhook", "events": ["shipment.created", "shipment.status_changed"]}' },
      { method: "GET", path: "/api/webhooks/config", desc: "Listar webhooks configurados", auth: true },
    ]
  }
];

const APIDocs = () => {
  const [copiedPath, setCopiedPath] = useState(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedPath(text);
    toast.success("Copiado");
    setTimeout(() => setCopiedPath(null), 2000);
  };

  const methodColor = { GET: "bg-green-100 text-green-700", POST: "bg-blue-100 text-blue-700", PATCH: "bg-yellow-100 text-yellow-700", DELETE: "bg-red-100 text-red-700" };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="api-docs-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-bold mb-4"><Code size={16} /> API para Desarrolladores</span>
          <h1 className="text-3xl md:text-4xl font-black text-[#5D4037]">Documentación API</h1>
          <p className="text-stone-600 mt-4 max-w-2xl mx-auto">Integra tu sistema con Todo de Cartón & ZipNGo. Usa nuestra API REST para cotizar envíos, generar guías, gestionar inventario y más.</p>
        </motion.div>

        <div className="bento-card p-6 mb-8 bg-[#2E7D32] text-white">
          <h3 className="font-bold mb-2">Autenticación</h3>
          <p className="text-sm text-white/80 mb-3">Todas las rutas protegidas requieren un header Authorization con tu token JWT:</p>
          <div className="bg-black/30 rounded-xl p-4 font-mono text-sm">
            <p className="text-green-400">Authorization: Bearer {"<tu_token_jwt>"}</p>
          </div>
        </div>

        {API_SECTIONS.map((section, si) => (
          <motion.div key={si} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: si * 0.05 }} className="mb-8">
            <h2 className="text-xl font-bold text-[#5D4037] mb-4">{section.title}</h2>
            <div className="space-y-3">
              {section.endpoints.map((ep, ei) => (
                <div key={ei} className="bento-card p-4" data-testid={`api-endpoint-${si}-${ei}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${methodColor[ep.method]}`}>{ep.method}</span>
                    <code className="text-sm font-mono text-[#5D4037] flex-1">{ep.path}</code>
                    <button onClick={() => copyToClipboard(ep.path)} className="p-1 hover:bg-stone-100 rounded">
                      {copiedPath === ep.path ? <CheckCircle size={16} className="text-green-500" /> : <Copy size={16} className="text-stone-400" />}
                    </button>
                    {ep.auth ? <Lock size={14} className="text-[#F57C00]" /> : <Unlock size={14} className="text-green-500" />}
                  </div>
                  <p className="text-sm text-stone-600">{ep.desc}</p>
                  {ep.params && <p className="text-xs text-stone-400 mt-1 font-mono">Params: {ep.params}</p>}
                  {ep.body && <div className="mt-2 bg-stone-50 rounded-lg p-2"><code className="text-xs text-stone-700 break-all">{ep.body}</code></div>}
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default APIDocs;
